import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText, ChevronDown, ChevronRight, AlertTriangle,
  CheckCircle, XCircle, Download, ExternalLink, RefreshCw,
  Receipt, Clock, Users, DollarSign,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('wmf_token')}`,
});

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

const getDefaultDates = () => {
  const today = new Date();
  const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastOfPrev = new Date(firstOfThisMonth - 1);
  const firstOfPrev = new Date(lastOfPrev.getFullYear(), lastOfPrev.getMonth(), 1);
  const fmt = (d) => d.toISOString().split('T')[0];
  return { start: fmt(firstOfPrev), end: fmt(lastOfPrev), invoiceDate: fmt(lastOfPrev) };
};

const fmtDate = (iso) =>
  iso ? new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const fmtCurrency = (n) =>
  n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—';

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

const apiPreview = async (body) => {
  const res = await fetch(`${API_URL}/invoicing/preview`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.msg || 'Preview failed');
  return data;
};

const apiRun = async (body) => {
  const res = await fetch(`${API_URL}/invoicing/run`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.msg || 'Run failed');
  return data;
};

const fetchRetreats = async () => {
  const res = await fetch(`${API_URL}/retreats`, { headers: authHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.msg || 'Failed to load retreats');
  return data.retreats;
};

const fetchRuns = async () => {
  const res = await fetch(`${API_URL}/invoicing/runs`, { headers: authHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.msg || 'Failed to load runs');
  return data.runs;
};

const fetchRun = async (id) => {
  const res = await fetch(`${API_URL}/invoicing/runs/${id}`, { headers: authHeaders() });
  const data = await res.json();
  if (!res.ok) throw new Error(data.msg || 'Failed to load run');
  return data.run;
};

const downloadExport = async (runId, filename) => {
  const res = await fetch(`${API_URL}/invoicing/runs/${runId}/export`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('wmf_token')}` },
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Small shared UI pieces
// ---------------------------------------------------------------------------

const StatCard = ({ icon: Icon, label, value, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
  };
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-3">
      <div className={`p-2 rounded-lg ${colors[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-lg font-semibold text-slate-900">{value}</p>
      </div>
    </div>
  );
};

const ModeToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
    {['therapy', 'songwriting'].map((m) => (
      <button
        key={m}
        onClick={() => onChange(m)}
        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
          value === m
            ? 'bg-white text-blue-700 shadow-sm border border-slate-200'
            : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        {m === 'therapy' ? 'Therapy' : 'Songwriting'}
      </button>
    ))}
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    completed: 'bg-green-100 text-green-700',
    failed:    'bg-red-100 text-red-700',
  };
  const icons = { completed: CheckCircle, failed: XCircle };
  const Icon = icons[status] || FileText;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
      <Icon className="h-3 w-3" />
      {status}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Preview panel
// ---------------------------------------------------------------------------

const InvoiceLinesTable = ({ lines }) => {
  if (!lines?.length) return <p className="text-sm text-slate-500 italic">No invoice lines.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left">
            <th className="pb-2 font-medium text-slate-600 pr-4">Student</th>
            <th className="pb-2 font-medium text-slate-600 pr-4">Description</th>
            <th className="pb-2 font-medium text-slate-600 text-right">Qty (hrs)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lines.map((l, i) => (
            <tr key={i} className="py-2">
              <td className="py-2 pr-4 font-medium text-slate-800 whitespace-nowrap">{l.student_name}</td>
              <td className="py-2 pr-4 text-slate-600 max-w-md">{l.description}</td>
              <td className="py-2 text-right tabular-nums text-slate-800">{l.quantity.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-slate-300">
            <td colSpan={2} className="pt-2 text-sm font-medium text-slate-600">Total</td>
            <td className="pt-2 text-right font-semibold text-slate-900 tabular-nums">
              {lines.reduce((s, l) => s + l.quantity, 0).toFixed(2)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const PayoutsTable = ({ payouts }) => {
  const [expanded, setExpanded] = useState({});
  if (!payouts?.length) return <p className="text-sm text-slate-500 italic">No payout data.</p>;
  return (
    <div className="space-y-1">
      {payouts.map((p, i) => (
        <div key={i} className="border border-slate-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setExpanded(e => ({ ...e, [i]: !e[i] }))}
            className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-slate-50 text-sm"
          >
            <div className="flex items-center gap-3">
              {expanded[i] ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
              <span className="font-medium text-slate-800">{p.therapist_name}</span>
            </div>
            <div className="flex items-center gap-6 text-slate-600">
              <span>{p.total_hours.toFixed(2)} hrs</span>
              <span>{p.rate != null ? `$${p.rate}/hr` : <span className="text-amber-600 font-medium">No rate</span>}</span>
              <span className="font-semibold text-slate-900">
                {p.total_amount != null ? fmtCurrency(p.total_amount) : <span className="text-amber-600">—</span>}
              </span>
            </div>
          </button>
          {expanded[i] && p.student_breakdown?.length > 0 && (
            <div className="bg-slate-50 border-t border-slate-200 px-4 py-3">
              <table className="w-full text-xs text-slate-600">
                <thead>
                  <tr className="text-left border-b border-slate-200 pb-1">
                    <th className="pb-1 font-medium">Student</th>
                    <th className="pb-1 font-medium text-right">Hours</th>
                    <th className="pb-1 font-medium text-right">Sessions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {p.student_breakdown.map((b, j) => (
                    <tr key={j}>
                      <td className="py-1">{b.student_name}</td>
                      <td className="py-1 text-right tabular-nums">{b.hours.toFixed(2)}</td>
                      <td className="py-1 text-right tabular-nums">{b.report_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const PreviewPanel = ({ preview, onGenerate, isGenerating }) => {
  const { summary, invoiceLines, therapistPayouts, warnings, memo, invoiceDate, dueDate } = preview;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [linesOpen, setLinesOpen] = useState(true);
  const [payoutsOpen, setPayoutsOpen] = useState(true);

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900">Preview</h3>
        <div className="text-xs text-slate-500">Invoice date: {fmtDate(invoiceDate)} · Due: {fmtDate(dueDate)}</div>
      </div>

      {warnings?.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Warnings</p>
            <ul className="mt-1 text-xs text-amber-700 list-disc list-inside space-y-0.5">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        </div>
      )}

      {summary?.total_reports === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center">
          <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No unpaid reports found in this date range.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={Receipt}  label="Reports"     value={summary.total_reports} color="blue" />
            <StatCard icon={Clock}    label="Total Hours" value={summary.total_hours?.toFixed(2)} color="blue" />
            <StatCard icon={Users}    label="Students"    value={invoiceLines?.length} color="green" />
            <StatCard icon={DollarSign} label="Est. Payout"
              value={summary.total_payout != null ? fmtCurrency(summary.total_payout) : 'Incomplete'}
              color={summary.total_payout != null ? 'green' : 'amber'} />
          </div>

          <p className="text-xs text-slate-500">Memo: <span className="italic">{memo}</span></p>

          {/* Invoice Lines */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setLinesOpen(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-700"
            >
              <span>Invoice Lines ({invoiceLines?.length} students)</span>
              {linesOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {linesOpen && <div className="p-4"><InvoiceLinesTable lines={invoiceLines} /></div>}
          </div>

          {/* Therapist Payouts */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setPayoutsOpen(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-700"
            >
              <span>Therapist Payouts ({therapistPayouts?.length} therapists)</span>
              {payoutsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {payoutsOpen && <div className="p-4"><PayoutsTable payouts={therapistPayouts} /></div>}
          </div>

          {/* Generate button */}
          {!confirmOpen ? (
            <div className="flex justify-end">
              <button
                onClick={() => setConfirmOpen(true)}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Generate Invoice in Wave
              </button>
            </div>
          ) : (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 flex items-center justify-between gap-4">
              <p className="text-sm text-blue-800">
                This will create a <strong>draft invoice</strong> in Wave and mark {summary.total_reports} reports as paid. Proceed?
              </p>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setConfirmOpen(false)}
                  className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 rounded-md bg-white"
                >
                  Cancel
                </button>
                <button
                  onClick={onGenerate}
                  disabled={isGenerating}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 flex items-center gap-2"
                >
                  {isGenerating && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
                  {isGenerating ? 'Generating…' : 'Confirm'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// New Run form
// ---------------------------------------------------------------------------

const NewRunTab = ({ onSuccess, mode, setMode, billBy, setBillBy, retreatId, setRetreatId }) => {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [invoiceDate, setInvoiceDate] = useState(defaults.invoiceDate);
  const [preview, setPreview] = useState(null);
  const [runError, setRunError] = useState(null);
  const [runSuccess, setRunSuccess] = useState(null);

  const { data: retreats } = useQuery({
    queryKey: ['retreats'],
    queryFn: fetchRetreats,
    enabled: mode === 'songwriting' && billBy === 'retreat',
  });

  const useRetreat = mode === 'songwriting' && billBy === 'retreat';

  const previewMutation = useMutation({
    mutationFn: apiPreview,
    onSuccess: (data) => { setPreview(data); setRunError(null); setRunSuccess(null); },
  });

  const runMutation = useMutation({
    mutationFn: apiRun,
    onSuccess: (data) => {
      setRunSuccess(data.run);
      setPreview(null);
      setRunError(null);
      onSuccess?.();
    },
    onError: (err) => { setRunError(err.message); },
  });

  const buildRequestBody = () =>
    useRetreat ? { mode, retreatId } : { mode, startDate, endDate, invoiceDate };

  const handlePreview = () => {
    setPreview(null);
    setRunError(null);
    setRunSuccess(null);
    previewMutation.mutate(buildRequestBody());
  };

  const handleGenerate = () => {
    runMutation.mutate(buildRequestBody());
  };

  // Sync invoiceDate to endDate when endDate changes (unless user manually changed it)
  const handleEndDateChange = (v) => {
    setEndDate(v);
    setInvoiceDate(v);
  };

  return (
    <div>
      {/* Form */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Billing Mode</label>
          <ModeToggle value={mode} onChange={(m) => { setMode(m); setPreview(null); }} />
        </div>

        {mode === 'songwriting' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Bill By</label>
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
              {[
                { id: 'retreat', label: 'Songwriting Retreat' },
                { id: 'dateRange', label: 'Custom Date Range' },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => { setBillBy(id); setPreview(null); }}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    billBy === id
                      ? 'bg-white text-blue-700 shadow-sm border border-slate-200'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {useRetreat ? (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Songwriting Retreat</label>
            <select
              value={retreatId}
              onChange={(e) => { setRetreatId(e.target.value); setPreview(null); }}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select a retreat…</option>
              {retreats?.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.startDate} – {r.endDate})
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Start Date', value: startDate, onChange: (v) => { setStartDate(v); setPreview(null); } },
              { label: 'End Date',   value: endDate,   onChange: (v) => { handleEndDateChange(v); setPreview(null); } },
              { label: 'Invoice Date', value: invoiceDate, onChange: (v) => { setInvoiceDate(v); setPreview(null); } },
            ].map(({ label, value, onChange }) => (
              <div key={label}>
                <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
                <input
                  type="date"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            ))}
          </div>
        )}

        {previewMutation.isError && (
          <p className="text-sm text-red-600">{previewMutation.error.message}</p>
        )}

        <div className="flex justify-end">
          <button
            onClick={handlePreview}
            disabled={previewMutation.isPending || (useRetreat && !retreatId)}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {previewMutation.isPending && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
            {previewMutation.isPending ? 'Loading preview…' : 'Preview'}
          </button>
        </div>
      </div>

      {/* Success banner */}
      {runSuccess && (
        <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-800">Invoice created successfully</p>
            <p className="text-xs text-green-700 mt-0.5">
              Run #{runSuccess.id} · {runSuccess.totalReports} reports marked paid.
            </p>
            {runSuccess.waveInvoiceViewUrl && (
              <a
                href={runSuccess.waveInvoiceViewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-green-700 underline hover:text-green-900"
              >
                View in Wave <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Run error */}
      {runError && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{runError}</p>
        </div>
      )}

      {/* Preview panel */}
      {preview && !runSuccess && (
        <PreviewPanel
          preview={preview}
          onGenerate={handleGenerate}
          isGenerating={runMutation.isPending}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Run History tab
// ---------------------------------------------------------------------------

const RunDetailModal = ({ runId, onClose }) => {
  const { data: run, isLoading, error } = useQuery({
    queryKey: ['invoicing-run', runId],
    queryFn: () => fetchRun(runId),
    enabled: !!runId,
  });

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadExport(runId, `invoicing_run_${runId}.csv`);
    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">
            Run #{runId} Detail
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none px-2">✕</button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {isLoading && <div className="text-center py-8 text-slate-500 text-sm">Loading…</div>}
          {error && <div className="text-center py-8 text-red-500 text-sm">{error.message}</div>}

          {run && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard icon={Receipt}    label="Reports"    value={run.totalReports} color="blue" />
                <StatCard icon={Clock}      label="Hrs"        value={run.totalHours?.toFixed(2)} color="blue" />
                <StatCard icon={DollarSign} label="Payout"     value={run.totalPayout != null ? fmtCurrency(run.totalPayout) : '—'} color="green" />
                <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${run.status === 'completed' ? 'bg-green-50' : 'bg-red-50'}`}>
                    {run.status === 'completed'
                      ? <CheckCircle className="h-5 w-5 text-green-700" />
                      : <XCircle className="h-5 w-5 text-red-700" />}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Status</p>
                    <p className="text-sm font-semibold text-slate-900 capitalize">{run.status}</p>
                  </div>
                </div>
              </div>

              <div className="text-xs text-slate-500 space-y-1">
                {run.retreatName ? (
                  <div><span className="font-medium">Retreat:</span> {run.retreatName} ({fmtDate(run.startDate)} – {fmtDate(run.endDate)})</div>
                ) : (
                  <div><span className="font-medium">Period:</span> {fmtDate(run.startDate)} – {fmtDate(run.endDate)}</div>
                )}
                <div><span className="font-medium">Invoice date:</span> {fmtDate(run.invoiceDate)}</div>
                <div><span className="font-medium">Created by:</span> {run.createdBy} · {run.createdAt ? new Date(run.createdAt).toLocaleString() : ''}</div>
                {run.waveInvoiceId && (
                  <div className="flex items-center gap-1">
                    <span className="font-medium">Wave invoice:</span>
                    {run.waveInvoiceViewUrl
                      ? <a href={run.waveInvoiceViewUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-0.5">{run.waveInvoiceId.slice(-12)} <ExternalLink className="h-3 w-3" /></a>
                      : run.waveInvoiceId}
                  </div>
                )}
              </div>

              {run.warnings?.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <ul className="text-xs text-amber-700 list-disc list-inside space-y-0.5">
                    {run.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Invoice Lines</h3>
                <InvoiceLinesTable lines={run.invoiceLines} />
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Therapist Payouts</h3>
                <PayoutsTable payouts={run.therapistPayouts} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const RunHistoryTab = () => {
  const { data: runs, isLoading, error, refetch } = useQuery({
    queryKey: ['invoicing-runs'],
    queryFn: fetchRuns,
  });
  const [selectedRunId, setSelectedRunId] = useState(null);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-slate-600">{runs?.length ?? 0} run{runs?.length !== 1 ? 's' : ''}</h3>
        <button onClick={() => refetch()} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {isLoading && <div className="text-center py-12 text-slate-500 text-sm">Loading…</div>}
      {error && <div className="text-center py-12 text-red-500 text-sm">{error.message}</div>}

      {!isLoading && !error && !runs?.length && (
        <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
          <Receipt className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No invoicing runs yet.</p>
        </div>
      )}

      {runs?.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-600">#</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-600">Mode</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-600">Period</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-600">Created</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-600">Reports</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-600">Hours</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-600">Payout</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-600">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {runs.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500 tabular-nums">{r.id}</td>
                  <td className="px-4 py-3 capitalize text-slate-700">{r.mode}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                    {r.retreatName || `${fmtDate(r.startDate)} – ${fmtDate(r.endDate)}`}
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}<br />
                    <span className="text-slate-400">{r.createdBy}</span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">{r.totalReports}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">{r.totalHours?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                    {r.totalPayout != null ? fmtCurrency(r.totalPayout) : <span className="text-amber-500">—</span>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setSelectedRunId(r.id)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedRunId && (
        <RunDetailModal runId={selectedRunId} onClose={() => setSelectedRunId(null)} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overdue retreats banner
// ---------------------------------------------------------------------------

const todayISO = () => new Date().toISOString().split('T')[0];

const OverdueRetreatsBanner = ({ onBillRetreat }) => {
  const { data: retreats } = useQuery({
    queryKey: ['retreats'],
    queryFn: fetchRetreats,
  });

  const overdue = (retreats || []).filter((r) => r.endDate < todayISO());
  if (!overdue.length) return null;

  return (
    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2">
      <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-amber-800">
          {overdue.length} songwriting retreat{overdue.length !== 1 ? 's' : ''} past its scheduled end date, not yet invoiced
        </p>
        <ul className="mt-1 text-xs text-amber-700 space-y-1">
          {overdue.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-2">
              <span>{r.name} (ended {fmtDate(r.endDate)})</span>
              <button
                onClick={() => onBillRetreat(r.id)}
                className="text-amber-800 font-medium underline hover:text-amber-900 flex-shrink-0"
              >
                Bill this retreat
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------

const InvoicingDashboard = () => {
  const [tab, setTab] = useState('new');
  const [mode, setMode] = useState('therapy');
  const [billBy, setBillBy] = useState('retreat'); // 'retreat' | 'dateRange' — songwriting mode only
  const [retreatId, setRetreatId] = useState('');
  const queryClient = useQueryClient();

  const handleRunSuccess = () => {
    setTab('history');
    queryClient.invalidateQueries({ queryKey: ['invoicing-runs'] });
    queryClient.invalidateQueries({ queryKey: ['retreats'] });
  };

  const handleBillRetreat = (id) => {
    setMode('songwriting');
    setBillBy('retreat');
    setRetreatId(String(id));
    setTab('new');
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Invoicing</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Generate Wave invoices and review therapist payouts for completed sessions.
        </p>
      </div>

      <OverdueRetreatsBanner onBillRetreat={handleBillRetreat} />

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-6">
          {[
            { id: 'new',     label: 'New Run' },
            { id: 'history', label: 'Run History' },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                tab === id
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'new' && (
        <NewRunTab
          onSuccess={handleRunSuccess}
          mode={mode}
          setMode={setMode}
          billBy={billBy}
          setBillBy={setBillBy}
          retreatId={retreatId}
          setRetreatId={setRetreatId}
        />
      )}
      {tab === 'history' && <RunHistoryTab />}
    </div>
  );
};

export default InvoicingDashboard;
